import MANIFEST from '../../public/assets/models/manifest.json'
import * as pc from 'playcanvas'

export interface CharacterEntry {
  file: string
  name: string
  animations: string[]
  runtime?: {
    dimensions?: [number, number, number]
    defaultScale?: number
    groundOffset?: number
    loadClass?: 'core' | 'zone' | 'deferred'
    /** Optional animation-only GLB loaded alongside this character on demand. */
    animationBank?: string
    /** Optional shared character atlas emitted by the licensed import pipeline. */
    atlas?: string
    [key: string]: unknown
  }
  source?: AssetSource
}

export interface AnimationBankEntry {
  file: string
  name: string
  animations: string[]
  runtime?: {
    loadClass?: 'core' | 'zone' | 'deferred'
    zone?: string
    boneCount?: number
    animationBytes?: number
    bindPose?: string
    bytes?: number
    [key: string]: unknown
  }
  source?: AssetSource
}

export interface RuntimeCollision {
  shape: 'box' | 'none'
  halfExtents?: [number, number, number]
}

export interface RuntimeAssetMetadata {
  /** Runtime bounds in PlayCanvas X/Y/Z metres at defaultScale. */
  dimensions: [number, number, number]
  /** Catalog-owned scale. Placements should not add corrective scale constants. */
  defaultScale: number
  /** Model-space vertical correction after conversion. Normalized assets use 0. */
  groundOffset: number
  collision: RuntimeCollision
  loadClass: 'core' | 'zone' | 'deferred'
  zone: string
  meshCount: number
  triangleCount: number
  materials: Array<'atlas' | 'glass'>
}

export interface AssetSource {
  vendor: string
  pack: string
  assetId: string
  licenseClass: 'proprietary-commercial-runtime'
  licenseRef: string
}

export interface PropEntry {
  file: string
  name: string
  kind: string
  runtime?: RuntimeAssetMetadata
  source?: AssetSource
}

export type PropKind =
  | 'desk'
  | 'chair'
  | 'monitor'
  | 'keyboard'
  | 'plant'
  | 'sofa'
  | 'shelf'
  | 'lamp'
  | 'cooler'
  | 'building'
  | 'street'
  | 'vehicle'
  | 'partition'
  | 'other'

export const MANIFEST_CHARACTERS = MANIFEST.characters as CharacterEntry[]
export const MANIFEST_PROPS = MANIFEST.props as PropEntry[]
export const MANIFEST_ANIMATION_BANKS: AnimationBankEntry[] = (
  MANIFEST as unknown as { animationBanks?: AnimationBankEntry[] }
).animationBanks ?? []

export const MODELS_BASE = '/assets/models/'

export function getCharacterEntry(file: string): CharacterEntry | undefined {
  return MANIFEST_CHARACTERS.find(c => c.file === file)
}

export function getAnimationBankEntry(file: string): AnimationBankEntry | undefined {
  return MANIFEST_ANIMATION_BANKS.find(bank => bank.file === file)
}

export function getPropEntry(file: string): PropEntry | undefined {
  return MANIFEST_PROPS.find(p => p.file === file)
}

/** Catalogued files grouped by their explicit runtime zone. */
export function getZoneAssetPlan(): Readonly<Record<string, readonly string[]>> {
  const plan: Record<string, string[]> = {}
  for (const prop of MANIFEST_PROPS) {
    if (prop.runtime?.loadClass !== 'zone') continue
    const files = plan[prop.runtime.zone] ?? []
    files.push(prop.file)
    plan[prop.runtime.zone] = files
  }
  return Object.fromEntries(
    Object.entries(plan).map(([zone, files]) => [
      zone,
      [...new Set(files)].sort(),
    ]),
  )
}

const _assetCache = new Map<string, pc.Asset>()
const _assetLoads = new Map<string, Promise<pc.Asset>>()
const _pendingCallbacks = new Map<string, Array<(asset: pc.Asset) => void>>()
const _assetRequestCounts = new Map<string, number>()
let _assetGeneration = 0

export interface AssetLoadDiagnostics {
  generation: number
  requests: Readonly<Record<string, number>>
}

/** Read-only app-generation request counters for QA and runtime observability. */
export function getAssetLoadDiagnostics(
  files?: readonly string[],
): AssetLoadDiagnostics {
  const selected = files ?? [..._assetRequestCounts.keys()]
  return {
    generation: _assetGeneration,
    requests: Object.fromEntries(
      selected.map((file) => [file, _assetRequestCounts.get(file) ?? 0]),
    ),
  }
}

class AssetLoadInvalidatedError extends Error {
  constructor(file: string) {
    super(`Asset load was invalidated during teardown: ${file}`)
    this.name = 'AssetLoadInvalidatedError'
  }
}

export function isAssetLoadInvalidated(error: unknown): boolean {
  return error instanceof AssetLoadInvalidatedError
}

export function canLoadAssets(app: pc.Application): boolean {
  const assets = (app as Partial<pc.Application>).assets
  return typeof assets?.loadFromUrl === 'function'
}

/**
 * Drop every cached asset + pending consumer. MUST be called when the
 * PlayCanvas app is destroyed: `app.destroy()` unloads every asset in its
 * registry, so a cached entry that survives points at an unloaded resource
 * whose meshes belong to a dead GraphicsDevice — instantiating from it
 * throws (or silently renders nothing) after a remount.
 */
export function resetAssetCache(): void {
  _assetGeneration += 1
  _assetCache.clear()
  _assetLoads.clear()
  _pendingCallbacks.clear()
  _assetRequestCounts.clear()
}

export function getCachedAsset(file: string): pc.Asset | undefined {
  const cached = _assetCache.get(file)
  // An asset can be unloaded out from under the cache; treat that as a miss.
  if (cached && !cached.resource) {
    _assetCache.delete(file)
    return undefined
  }
  return cached
}

/**
 * Call `cb` immediately if the asset is already cached, otherwise queue it
 * to be called once the asset finishes loading via `preloadAssets` or
 * `ensureAssetLoaded`.
 */
export function onAssetReady(file: string, cb: (asset: pc.Asset) => void): void {
  const cached = _assetCache.get(file)
  if (cached) { cb(cached); return }
  const list = _pendingCallbacks.get(file) ?? []
  list.push(cb)
  _pendingCallbacks.set(file, list)
}

function loadGlb(app: pc.Application, file: string): Promise<pc.Asset> {
  const cached = getCachedAsset(file)
  if (cached) return Promise.resolve(cached)

  const active = _assetLoads.get(file)
  if (active) return active

  const generation = _assetGeneration
  const load = new Promise<pc.Asset>((resolve, reject) => {
    const url = `${MODELS_BASE}${file}`
    if (!canLoadAssets(app)) {
      reject(new Error('PlayCanvas asset registry is unavailable'))
      return
    }
    _assetRequestCounts.set(file, (_assetRequestCounts.get(file) ?? 0) + 1)
    app.assets.loadFromUrl(url, 'container', (err: string | null, asset?: pc.Asset) => {
      // PlayCanvas may finish a request while app.destroy() is running. Never
      // let that old registry repopulate the module cache or notify consumers
      // belonging to the replacement app.
      if (generation !== _assetGeneration) {
        reject(new AssetLoadInvalidatedError(file))
        return
      }
      if (err) {
        _pendingCallbacks.delete(file)
        reject(new Error(String(err)))
        return
      }
      if (!asset) {
        _pendingCallbacks.delete(file)
        reject(new Error(`No asset returned for ${file}`))
        return
      }
      _assetCache.set(file, asset)
      const cbs = _pendingCallbacks.get(file) ?? []
      _pendingCallbacks.delete(file)
      for (const cb of cbs) {
        // A throwing consumer must not mark the ASSET load as failed (the
        // registry treats callback exceptions as load errors, which cascades
        // into a permanently stuck preloader).
        try { cb(asset) } catch (e) { console.warn(`[assets] consumer failed for ${file}:`, e) }
      }
      resolve(asset)
    })
  })
  _assetLoads.set(file, load)
  void load.then(
    () => {
      if (_assetLoads.get(file) === load) _assetLoads.delete(file)
    },
    () => {
      if (_assetLoads.get(file) === load) _assetLoads.delete(file)
    },
  )
  return load
}

/**
 * Load a catalog asset on demand. Zone/deferred assets intentionally skip the
 * global startup preload and should enter the world through this function.
 * Concurrent requests for the same file share one registry load.
 */
export function ensureAssetLoaded(app: pc.Application, file: string): Promise<pc.Asset> {
  if (
    !getCharacterEntry(file) &&
    !getAnimationBankEntry(file) &&
    !getPropEntry(file)
  ) {
    return Promise.reject(new Error(`Asset is not present in the runtime catalog: ${file}`))
  }
  return loadGlb(app, file)
}

/**
 * Release a zone-owned source asset after every instantiated render entity has
 * been destroyed. `expected` prevents a stale generation from unloading a
 * newer registry resource for the same catalog file.
 */
export function releaseAsset(
  app: pc.Application,
  file: string,
  expected?: pc.Asset,
): boolean {
  const cached = _assetCache.get(file)
  if (cached && expected && cached !== expected) return false
  const asset = expected ?? cached
  if (!asset) return false

  if (cached === asset) _assetCache.delete(file)
  _pendingCallbacks.delete(file)
  try { asset.unload() } catch { /* application teardown may already own it */ }
  try { app.assets.remove(asset) } catch { /* registry may already be destroyed */ }
  return true
}

/**
 * Preload GLBs in priority order so what the player sees first loads first:
 * batch 1 = the original desk characters + core desk furniture, batch 2 =
 * everything else. Batches load sequentially to avoid 40-way request
 * contention on startup; within a batch, loads are parallel.
 */
export interface PreloadProgress {
  loaded: number
  total: number
  /** True once the visible-first batch (desk characters + core furniture) is in. */
  coreReady: boolean
  done: boolean
}

export interface PreloadPlan {
  core: string[]
  background: string[]
}

/**
 * Produce the global preload plan without pulling zone/deferred catalog items
 * into startup. Keeping this pure makes the load boundary straightforward to
 * audit and test as the licensed catalog grows.
 */
export function getPreloadPlan(): PreloadPlan {
  const CORE_PROP_KINDS = new Set(['building', 'street'])
  const CORE_CHARACTERS = new Set<string>([])
  const characters = MANIFEST_CHARACTERS
    .filter(c => !c.runtime || c.runtime.loadClass === 'core')
    .map(c => ({
      file: c.file,
      core: CORE_CHARACTERS.has(c.file),
    }))
  const props = MANIFEST_PROPS
    .filter(p => !p.runtime || p.runtime.loadClass === 'core')
    .map(p => ({
      file: p.file,
      core: p.runtime?.loadClass === 'core' || CORE_PROP_KINDS.has(p.kind),
    }))
  const all = [...characters, ...props]
  return {
    core: all.filter(a => a.core).map(a => a.file),
    background: all.filter(a => !a.core).map(a => a.file),
  }
}

export async function preloadAssets(
  app: pc.Application,
  onProgress?: (p: PreloadProgress) => void,
  signal?: AbortSignal,
): Promise<void> {
  // Buildings are the first thing the player sees on spawn — they load in the
  // first batch. Desk furniture is inside a building and can arrive later.
  // ONLY what is visible from the spawn point at the gate. Characters are the
  // heaviest assets by far (~1.85 MB of a 2.11 MB blocking batch) and every
  // one of them is inside a building the player has not reached yet; desk
  // furniture likewise. Blocking on them delayed first paint by seconds for
  // things nobody can see.
  const { core: batch1, background: batch2 } = getPreloadPlan()

  const total = batch1.length + batch2.length
  let loaded = 0
  let coreReady = false
  const report = () => {
    if (!signal?.aborted) {
      onProgress?.({ loaded, total, coreReady, done: loaded >= total })
    }
  }
  const tracked = (f: string) =>
    loadGlb(app, f)
      .then((a) => {
        loaded += 1
        report()
        return a
      })
      .catch((e) => {
        if (signal?.aborted || isAssetLoadInvalidated(e)) return null
        // One bad asset must never hang "SETTING UP THE OFFICE" — count it,
        // log it, move on (spawners degrade gracefully without it).
        loaded += 1
        console.warn(`[assets] failed to load ${f}:`, e)
        report()
        return null
      })

  report()
  await Promise.all(batch1.map(tracked))
  if (signal?.aborted) return
  coreReady = true
  report()
  await Promise.all(batch2.map(tracked))
}
